import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/utils/toast';
import i18n from '@/i18n';
import { variationsService } from '@/services/variations.service';
import {
  PostVariation,
  CreateVariationData,
  UpdateVariationData,
  DuplicateVariationData,
  UpdateVariationMediaData,
  ReorderMediaData,
} from '@/types/variations';
import { formatApiError } from '@/services/api';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { invalidateReleaseState } from '@/lib/invalidateRelease';

/**
 * React Query hooks for post variations
 */

/**
 * Fetch all variations for a post
 */
export const useVariations = (postId: number) => {
  return useQuery({
    queryKey: ['variations', postId],
    queryFn: () => variationsService.getVariations(postId),
    enabled: !!postId && postId > 0,
  });
};

/**
 * Fetch a single variation
 */
export const useVariation = (postId: number, variationId: number) => {
  return useQuery<PostVariation, Error>({
    queryKey: ['variations', postId, variationId],
    queryFn: () => variationsService.getVariation(postId, variationId),
    enabled: !!postId && postId > 0 && !!variationId && variationId > 0,
  });
};

/**
 * Create a new variation
 */
export const useCreateVariation = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: ({
      postId,
      data,
    }: {
      postId: number;
      data: CreateVariationData;
    }) => variationsService.createVariation(postId, data),
    onSuccess: (_, variables) => {
      toast.success(i18n.t('variation_created_success'));
      invalidateReleaseState(queryClient, { teamId, postId: variables.postId });
    },
    onError: (error) => {
      toast.error(formatApiError(error).message);
    },
  });
};

/**
 * Update an existing variation
 */
export const useUpdateVariation = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: ({
      postId,
      variationId,
      data,
    }: {
      postId: number;
      variationId: number;
      data: UpdateVariationData;
    }) => variationsService.updateVariation(postId, variationId, data),
    onSuccess: (_, variables) => {
      toast.success(i18n.t('variation_updated_success'));
      invalidateReleaseState(queryClient, { teamId, postId: variables.postId, variationId: variables.variationId });
    },
    onError: (error) => {
      toast.error(formatApiError(error).message);
    },
  });
};

/**
 * Delete a variation
 */
export const useDeleteVariation = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: ({
      postId,
      variationId,
    }: {
      postId: number;
      variationId: number;
    }) => variationsService.deleteVariation(postId, variationId),
    onSuccess: (_, variables) => {
      toast.success(i18n.t('variation_deleted_success'));
      invalidateReleaseState(queryClient, { teamId, postId: variables.postId, variationId: variables.variationId });
    },
    onError: (error) => {
      toast.error(formatApiError(error).message);
    },
  });
};

/**
 * Duplicate a variation
 */
export const useDuplicateVariation = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: ({
      postId,
      variationId,
      data,
    }: {
      postId: number;
      variationId: number;
      data: DuplicateVariationData;
    }) => variationsService.duplicateVariation(postId, variationId, data),
    onSuccess: (_, variables) => {
      invalidateReleaseState(queryClient, { teamId, postId: variables.postId, variationId: variables.variationId });
    },
    onError: (error) => {
      toast.error(formatApiError(error).message);
    },
  });
};

/**
 * Update variation media (replace all media)
 */
export const useUpdateVariationMedia = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: ({
      postId,
      variationId,
      data,
    }: {
      postId: number;
      variationId: number;
      data: UpdateVariationMediaData;
    }) => variationsService.updateVariationMedia(postId, variationId, data),
    onSuccess: (_, variables) => {
      invalidateReleaseState(queryClient, { teamId, postId: variables.postId, variationId: variables.variationId });
    },
    onError: (error) => {
      toast.error(formatApiError(error).message);
    },
  });
};

/**
 * Reorder variation media
 */
export const useReorderVariationMedia = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: ({
      postId,
      variationId,
      data,
    }: {
      postId: number;
      variationId: number;
      data: ReorderMediaData;
    }) => variationsService.reorderVariationMedia(postId, variationId, data),
    onSuccess: (_, variables) => {
      invalidateReleaseState(queryClient, { teamId, postId: variables.postId, variationId: variables.variationId });
    },
    onError: (error) => {
      toast.error(formatApiError(error).message);
    },
  });
};

/**
 * Fetch all variations for the team (across all posts)
 */
export const useTeamVariations = (params?: {
  type?: 'language' | 'custom';
  contentStatus?: 'DRAFT' | 'READY';
  search?: string;
  labels?: string;
  language?: string;
  workflowStatus?: 'PENDING' | 'APPROVED' | 'CHANGES_REQUESTED';
  postId?: number;
  page?: number;
  limit?: number;
}) => {
  return useQuery({
    queryKey: ['team-variations', params],
    queryFn: () => variationsService.getTeamVariations(params),
    staleTime: 30 * 1000, // 30 seconds
  });
};
